

/**
 * Нормалізує JSON/string поле для збереження
 * Для json та encrypted типів - конвертує об'єкт в JSON-строку, якщо це string - залишає як є
 */
function normalizeJsonField(raw: any): string {
  if (typeof raw === "string") {
    return raw;
  } else if (raw !== null && raw !== undefined) {
    return JSON.stringify(raw);
  }
  return raw as any;
}

/**
 * Нормалізує redirect поле (plain string)
 * Якщо користувач помилково залишив JSON - намагається витягти поле redirect/url/offerUrl/message
 */
function normalizeRedirectField(value: any, defaultFallback: string): string {
  let v = (value ?? "").toString().trim();
  
  // Якщо користувач помилково залишив JSON - спробуємо витягти поле
  if (v.startsWith("{") || v.startsWith("[")) {
    try {
      const parsed = JSON.parse(v);
      v = parsed?.redirect || parsed?.offerUrl || parsed?.url || parsed?.message || v;
    } catch {
      // залишаємо як є
    }
  }
  
  return v || defaultFallback;
}

/**
 * Нормалізує offerUrl залежно від типу відповіді
 */
function normalizeOfferUrl(
  offerUrl: any,
  responseType: string,
  encryptionAlgorithm?: string
): { offerUrl: string; encryptionAlgorithm?: string } {
  if (responseType === "redirect") {
    return {
      offerUrl: normalizeRedirectField(offerUrl, "{{OFFER_URL}}"),
    };
  } else {
    // Для json та encrypted - однакова логіка
    const result: { offerUrl: string; encryptionAlgorithm?: string } = {
      offerUrl: normalizeJsonField(offerUrl),
    };
    
    // Додаємо алгоритм шифрування тільки для encrypted
    if (responseType === "encrypted") {
      result.encryptionAlgorithm = encryptionAlgorithm || "aes-256-cbc";
    }
    
    return result;
  }
}

/**
 * Нормалізує offerError залежно від типу помилки
 */
function normalizeOfferError(
  offerError: any,
  errorType: string,
  errorStaticSiteId?: string,
  errorLandingIndexPath?: string
): {
  offerError: string;
  errorStaticSiteId?: string;
  errorLandingIndexPath?: string;
} {
  if (errorType === "render") {
    const staticSiteId = (errorStaticSiteId ?? "").toString().trim();
    const indexPath = (errorLandingIndexPath || "index.html").toString().trim();
    return {
      offerError: JSON.stringify({ staticSiteId, indexPath }),
      errorStaticSiteId: staticSiteId,
      errorLandingIndexPath: indexPath,
    };
  } else if (errorType === "json" || errorType === "encrypted") {
    // Для json та encrypted - однакова логіка
    return {
      offerError: normalizeJsonField(offerError),
    };
  } else {
    // redirect => plain string
    return {
      offerError: normalizeRedirectField(offerError, ""),
      errorStaticSiteId: "",
      errorLandingIndexPath: "index.html",
    };
  }
}

/**
 * Нормалізує список endpoint mappings перед збереженням
 * Виконує всі необхідні перетворення полів залежно від їх типів
 */
export function sanitizeForSave(list: any[]) {
  return list.map((m) => {
    const dto: any = {
      handler: m.handler,
      path: m.path,
      method: m.method,
      enabled: !!m.enabled,
      subdomain: m.subdomain || "",
      isOffer: !!m.isOffer,
      offerResponseType: m.offerResponseType || "redirect",
      offerResponseCode: Number(m.offerResponseCode) || 200,
      offerErrorType: m.offerErrorType || "redirect",
      offerErrorCode: Number(m.offerErrorCode) || 400,
    };

    // === OFFER URL normalization ===
    const offerUrlResult = normalizeOfferUrl(
      m.offerUrl,
      dto.offerResponseType,
      m.encryptionAlgorithm
    );
    dto.offerUrl = offerUrlResult.offerUrl;
    if (offerUrlResult.encryptionAlgorithm) {
      dto.encryptionAlgorithm = offerUrlResult.encryptionAlgorithm;
    }

    if (!dto.encryptionAlgorithm && dto.offerErrorType === "encrypted") {
      dto.encryptionAlgorithm = m.encryptionAlgorithm || "aes-256-cbc";
    }

    // === Response-in-header (payload у кастомному хедері) ===
    // Актуально лише для json / encrypted; імʼя порожнє = дефолт на бекенді.
    if (m.responseInHeader) {
      dto.responseInHeader = true;
      const headerName = String(m.responseHeaderName || "").trim();
      if (headerName) {
        dto.responseHeaderName = headerName;
      }
    }

    // === OFFER ERROR normalization ===
    const offerErrorResult = normalizeOfferError(
      m.offerError,
      dto.offerErrorType,
      m.errorStaticSiteId,
      m.errorLandingIndexPath
    );
    dto.offerError = offerErrorResult.offerError;
    if (offerErrorResult.errorStaticSiteId !== undefined) {
      dto.errorStaticSiteId = offerErrorResult.errorStaticSiteId;
    }
    if (offerErrorResult.errorLandingIndexPath !== undefined) {
      dto.errorLandingIndexPath = offerErrorResult.errorLandingIndexPath;
    }

    return dto;
  });
}

